"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image, { type StaticImageData } from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { usePathname } from "next/navigation";
import type { SolutionPageContent } from "@/app/servicos/modernizacao-de-sistemas-legados/content/content";
import DifferencesLeft from "~/public/assets/backgrounds/bg-flow-left.png";
import DifferencesRight from "~/public/assets/backgrounds/bg-flow-right.png";
import Bg_Section from "~/public/assets/backgrounds/bg-section-bottom.png";
import DifferenceDots from "~/public/assets/services/differences-dots-card.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";

type ServicePageTemplateProps = {
	service: ServicePageContent | SolutionPageContent;
};

type DifferencesItemCardProps = {
	cards: {
		image: StaticImageData;
		title: string;
	};
	isSolutionsPage: boolean;
};

function DifferencesItemCard({
	cards,
	isSolutionsPage,
}: DifferencesItemCardProps) {
	return (
		<div
			data-service-card
			className={`relative h-full! flex flex-col gap-8 p-8 pt-0 rounded-2xl
			${isSolutionsPage ? "bg-[#0A0A0A99] border border-gray-300" : "bg-[#11111180]"}`}
		>
			<Image
				src={DifferenceDots}
				alt="Fundo de pontos para decoração"
				className="w-full max-w-52.75 absolute left-0 top-0 bottom-0 z-10"
				width={2000}
				height={2000}
			/>
			<Image
				data-service-card-media
				src={cards.image}
				alt={cards.title}
				className="w-full h-fit! relative z-20"
			/>
			<p
				className={`text-base mt-auto md:text-xl 3xl:text-[22px] whitespace-pre-line
			${isSolutionsPage ? "max-w-75 3xl:max-w-105" : "max-w-75 3xl:max-w-90"}`}
			>
				{cards.title}
			</p>
		</div>
	);
}

export default function DifferencesServicesSection({
	service,
}: ServicePageTemplateProps) {
	const sectionRef = useRef<HTMLElement | null>(null);
	const pathname = usePathname();

	const isSolutionsPage = pathname.includes("modernizacao-de-sistemas-legados");

	useServiceCardsAnimation(sectionRef, {
		preset: "cascade",
	});

	return (
		<section
			ref={sectionRef}
			className={`md:container-padding relative border-b border-gray-300 text-white 
			${isSolutionsPage ? "" : "bg-gray-1100"}`}
		>
			{isSolutionsPage ? (
				<Image
					src={Bg_Section}
					alt="Background decorativo"
					className="absolute w-full h-fit top-0 pointer-events-none max-md:object-cover scale-y-[-1]"
					priority={false}
					width={1440}
					height={500}
				/>
			) : (
				<>
					<Image
						src={DifferencesLeft}
						alt=""
						aria-hidden="true"
						className="hidden 2xl:block h-auto w-full max-w-[8%] 3xl:max-w-[9.7%] max-h-[70%] top-0 left-0 absolute"
					/>
					<Image
						src={DifferencesRight}
						alt=""
						aria-hidden="true"
						className="hidden 2xl:block h-auto w-full max-w-[8.5%] 3xl:max-w-[9.7%] max-h-[70%] top-0 right-0 absolute"
					/>
				</>
			)}

			<div className="container md:relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-18 lg:py-22">
				<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />
				<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />

				<div className="flex flex-col text-center">
					<p className="tag">{service.differences?.tag}</p>
					<h2 className="title">{service.differences?.title}</h2>
					<p className="description mt-4 max-w-150 mx-auto">
						{service.differences?.quote}
					</p>
				</div>

				<div data-service-cards-animation-trigger className="block md:hidden">
					<Swiper
						modules={[A11y, Navigation]}
						navigation={{
							prevEl: ".comparison-prev",
							nextEl: ".comparison-next",
						}}
						slidesPerView="auto"
						spaceBetween={28}
						className="comparison-swiper mt-10 overflow-visible! [&_.swiper-wrapper]:h-fit!"
					>
						{service.differences?.cards.map((cards) => (
							<SwiperSlide
								key={cards.image.src}
								className="h-auto! max-w-[92%]"
							>
								<DifferencesItemCard
									cards={cards}
									isSolutionsPage={isSolutionsPage}
								/>
							</SwiperSlide>
						))}
					</Swiper>
					<div className="mt-5 flex items-center gap-5 relative z-20">
						<button
							aria-label="Item anterior"
							className="comparison-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
							type="button"
						>
							<ArrowLeft
								className="size-9 text-primary-300"
								strokeWidth={1.5}
							/>
						</button>
						<button
							aria-label="Próximo item"
							className="comparison-next cursor-pointer text-white transition-all duration-300 ease-in-out"
							type="button"
						>
							<ArrowRight
								className="size-9 text-primary-300"
								strokeWidth={1.5}
							/>
						</button>
					</div>
				</div>

				<div
					data-service-cards-animation-trigger
					className="mt-16 md:grid grid-cols-2 gap-9 hidden"
				>
					{service.differences?.cards.map((cards) => (
						<DifferencesItemCard
							key={cards.image.src}
							cards={cards}
							isSolutionsPage={isSolutionsPage}
						/>
					))}
				</div>
			</div>
		</section>
	);
}
